/**
 * RegionalTeachersRoster — the Roster tab of the Teachers bucket: KPI strip
 * over the 1.5fr/1fr grid (BON-83 design). Split out of RegionalTeachers.tsx
 * when the tab shell landed (BON-240) to stay under the lint size caps.
 *
 * Live: roster counts, availability (shift/available), open shifts,
 * POS sales (warehouse, null → em-dash). Mocked pending BON-144:
 * payroll KPIs, Rate column, assistant split, PTO cells, resupply.
 */
import { useMemo, useState } from 'react';

import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, SegmentedControl } from '../primitives';

import { BucketError, BucketLoading, SectionCard } from './BucketStates';
import AssistantsGrid from './components/roster/AssistantsGrid';
import AvailabilityLegend from './components/roster/AvailabilityLegend';
import AvailabilityTable from './components/roster/AvailabilityTable';
import TeacherRosterTable from './components/roster/TeacherRosterTable';
import { useRegionalTeachers, type TeacherEntry } from './regionalApi';
import { RegionalTeachersOpenShifts } from './RegionalTeachersOpenShifts';
import { RegionalTeachersResupply } from './RegionalTeachersResupply';

type SortKey = 'workshopsMtd' | 'posSalesMtd' | 'workshopsThisWeek';

// Design sort labels (regional-teachers.jsx).
const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: 'workshopsMtd', label: 'Workshops/mo' },
  { value: 'posSalesMtd', label: 'POS $' },
  { value: 'workshopsThisWeek', label: 'This week' },
];

function sortTeachers(arr: ReadonlyArray<TeacherEntry>, key: SortKey): TeacherEntry[] {
  return [...arr].sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0));
}

export default function RegionalTeachersRoster({ regionSlug }: { regionSlug: string }) {
  const [sortKey, setSortKey] = useState<SortKey>('workshopsMtd');
  const q = useRegionalTeachers(regionSlug);

  const sorted = useMemo(
    () => sortTeachers(q.data?.data.teachers ?? [], sortKey),
    [q.data, sortKey],
  );

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <>
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 14 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <SectionCard
            title="Teacher Roster"
            action={
              <SegmentedControl
                value={sortKey}
                onChange={setSortKey}
                options={SORT_OPTIONS}
                ariaLabel="Sort teachers"
              />
            }
          >
            <TeacherRosterTable teachers={sorted} />
          </SectionCard>
          <SectionCard title="Assistant Roster">
            <AssistantsGrid assistants={data.assistants} />
          </SectionCard>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <SectionCard
            title="Availability · This Week"
            action={
              <AvailabilityLegend
                declaredCount={data.availabilityDeclaredCount ?? 0}
                total={data.teachers.length}
              />
            }
          >
            <AvailabilityTable teachers={sorted} availability={data.availability} />
          </SectionCard>
          {/* `?? []` tolerates an older backend DTO during the deploy window
              (new frontend / not-yet-deployed getTeachers). */}
          <RegionalTeachersOpenShifts openShifts={data.openShifts ?? []} />
          <RegionalTeachersResupply />
        </div>
      </div>
    </>
  );
}
